// The appliance robots as rigid part geometries (local space: feet at y=0, facing -Z).
// Built once at startup from primitives + decal quads from the shared atlas (decals.ts);
// big silhouette parts carry a baked ink hull. Proportions follow the enemies/ sheet:
// one big appliance body, 2-5 moving bits, two or three inks on cream, tiny eyes.
// `lod` (0 low .. 2 full) trims segment counts and the small hulls for the quality tiers.

import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { PAL } from '../palette';
import { DC, type Cell } from './decals';
import { PartBuilder, T, type Num3, type SubOpts } from './kit';

const { BoxGeometry: Box, CylinderGeometry: Cyl, SphereGeometry: Sph, TorusGeometry: Tor, LatheGeometry: Lathe } = THREE;
const V2 = (x: number, y: number) => new THREE.Vector2(x, y);

let LOD = 2;
/** segment count at the current lod */
const sg = (n: number) => (LOD >= 2 ? n : Math.max(4, Math.round(n * (LOD === 1 ? 0.75 : 0.55))));
/** outline for small parts only at full lod */
const SMALL: SubOpts = {};
const OUT: SubOpts = { outline: true };
const smallOut = (): SubOpts => (LOD >= 2 ? OUT : SMALL);

// ---------------------------------------------------------------- helpers
function box(w: number, h: number, d: number, x = 0, y = 0, z = 0, rx = 0, ry = 0, rz = 0, ys = 1) {
  return T(new Box(w, h, d, 1, ys, 1), x, y, z, rx, ry, rz);
}
function rbox(w: number, h: number, d: number, r: number, x = 0, y = 0, z = 0, rx = 0, ry = 0, rz = 0, seg = 1) {
  return T(new RoundedBoxGeometry(w, h, d, LOD >= 2 ? seg : 1, r), x, y, z, rx, ry, rz);
}
/** cylinder along Y */
function cyl(rt: number, rb: number, h: number, seg: number, x = 0, y = 0, z = 0, rx = 0, ry = 0, rz = 0, hs = 1) {
  return T(new Cyl(rt, rb, h, sg(seg), hs), x, y, z, rx, ry, rz);
}
/** cylinder whose axis points along Z (discs facing front) */
function disc(r: number, h: number, seg: number, x: number, y: number, z: number) {
  return T(new Cyl(r, r, h, sg(seg)), x, y, z, Math.PI / 2);
}
/** cylinder whose axis points along X (wheels) */
function axle(r: number, h: number, seg: number, x: number, y: number, z: number) {
  return T(new Cyl(r, r, h, sg(seg)), x, y, z, 0, 0, Math.PI / 2);
}
/** a stick from a to b */
function tube(ax: number, ay: number, az: number, bx: number, by: number, bz: number, r: number, seg = 5, r2 = r) {
  const a = new THREE.Vector3(ax, ay, az), b = new THREE.Vector3(bx, by, bz);
  const d = b.clone().sub(a);
  const g = new Cyl(r2, r, d.length(), seg);
  const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), d.clone().normalize());
  g.applyQuaternion(q);
  const m = a.add(b).multiplyScalar(0.5);
  g.translate(m.x, m.y, m.z);
  return g;
}
/** a decal quad from the atlas; ry = PI faces front (-Z), PI/2 faces +X, -PI/2 faces -X, 0 back */
function decal(w: number, h: number, x: number, y: number, z: number, ry = Math.PI, rz = 0, rx = 0) {
  return T(new THREE.PlaneGeometry(w, h), x, y, z, rx, ry, rz);
}
/** add a decal sub (vertex colour white = atlas colour as painted) */
function addDecal(b: PartBuilder, cell: Cell, g: THREE.BufferGeometry, rust = 0.25, tint: number = 0xffffff) {
  b.add(g, tint, { decal: cell, rust, chip: 0.25, jitter: 0 });
}
/** decal wrapped on a body of revolution of radius r (front side), centred at (x, y) */
function decalOn(r: number, w: number, h: number, x: number, y: number, rz = 0) {
  const z = -Math.sqrt(Math.max(r * r - x * x, 0.0001)) - 0.008;
  return decal(w, h, x, y, z, Math.PI - Math.asin(x / r), rz);
}
/** rust coverage ramp: `lo` at/below y0, `hi` at/above y1 */
const ramp = (y0: number, lo: number, y1: number, hi: number): Num3 => (_x, y) =>
  y <= y0 ? lo : y >= y1 ? hi : lo + ((y - y0) / (y1 - y0)) * (hi - lo);

/** the value anchor: warm near-black for chassis, legs, treads, wheels (every robot's darkest dark) */
export const DARK = 0x2a2420;
/** a step up from DARK for joints and hubs so the running gear still reads as parts */
const DARK2 = 0x40362c;
/** flip a geometry inside out (an open sleeve seen from its mouth shows its inner wall) */
function inward(g0: THREE.BufferGeometry) {
  const g = g0.index ? g0.toNonIndexed() : g0;
  for (const name of Object.keys(g.attributes)) {
    const at = g.attributes[name], a = at.array as Float32Array, w = at.itemSize;
    for (let t = 0; t < a.length; t += w * 3) for (let k = 0; k < w; k++) { const x = a[t + w + k]; a[t + w + k] = a[t + 2 * w + k]; a[t + 2 * w + k] = x; }
  }
  const n = g.attributes.normal.array as Float32Array;
  for (let i = 0; i < n.length; i++) n[i] = -n[i];
  return g;
}

export interface RobotModel {
  parts: Record<string, THREE.BufferGeometry>;
}

export function setLod(q: number) { LOD = q <= 0 ? 0 : q === 1 ? 1 : 2; }

// ---------------------------------------------------------------- eye (shared by all)
export function buildEye() {
  const b = new PartBuilder(0.16);
  // deadpan: a dull cream socket and a big flat dark dot, no gloss highlight
  b.add(new Sph(1, sg(8), sg(6)), 0xd9cdb0, { outline: true, rust: 0.2, chip: 0, jitter: 0.02 });
  b.add(T(new Sph(0.62, sg(6), 4), 0, 0, -0.55, 0, 0, 0, 1, 1, 0.55), DARK, { rust: 0, chip: 0, jitter: 0 });
  return b.build();
}

/** primed diamond: white core (tinted per instance) + baked ink hull */
export function buildDiamond() {
  const b = new PartBuilder(0.16);
  b.add(T(new THREE.OctahedronGeometry(1, 0), 0, 0, 0, 0, 0, 0, 0.62, 1, 0.62), 0xffffff, { outline: true, jitter: 0 });
  return b.build();
}

// ---------------------------------------------------------------- 0 Swarmer: toaster
export function buildSwarmer(): RobotModel {
  const red = PAL.toasterRed, plum = PAL.plum;
  const body = new PartBuilder(0.03).ao(0.2, 0.75);
  // the shell: red front, plum left flank (the ref's two inks), rust heavy low and at the rim
  const tone: Num3 = (x) => (x < -0.47 ? plum : red);
  body.add(rbox(1.06, 0.78, 0.64, 0.08, 0, 0.67, 0), tone, { outline: true, rust: (_x, y) => (y < 0.42 ? 0.7 : y > 0.98 ? 0.55 : 0.32), chip: 0.7 });
  // chrome-ish crimped base trim (cream) + dark undercarriage
  body.add(rbox(1.12, 0.09, 0.7, 0.03, 0, 0.27, 0, 0, 0, 0, 1), PAL.tan, { outline: true, rust: 0.6, chip: 0.6 });
  body.add(box(0.98, 0.1, 0.56, 0, 0.19, 0), DARK, OUT);
  // corner bolts + a crimp line along the front
  for (const bx of [-0.46, 0.46]) for (const by of [0.36, 0.98]) body.add(T(new THREE.OctahedronGeometry(0.022, 0), bx, by, -0.325), PAL.inkDark, { rust: 0.8 });
  body.add(box(0.94, 0.012, 0.012, 0, 0.33, -0.326), PAL.inkDark);
  // toast slots
  body.add(box(0.42, 0.02, 0.14, -0.24, 1.065, 0), PAL.inkDark);
  body.add(box(0.42, 0.02, 0.14, 0.24, 1.065, 0), PAL.inkDark);
  // the grimace (big, it's the read at range) + stickers
  addDecal(body, DC.grimace, decal(0.96, 0.5, 0.02, 0.55, -0.329), 0.15);
  addDecal(body, DC.asis, decal(0.17, 0.17, -0.34, 0.37, -0.329, Math.PI, 0.1), 0.4);
  addDecal(body, DC.hanko, decal(0.1, 0.1, 0.4, 0.95, -0.329, Math.PI, -0.05), 0.2);
  addDecal(body, DC.tagCream, decal(0.42, 0.21, 0.0, 0.66, 0.33, 0), 0.35);
  addDecal(body, DC.patch, decal(0.2, 0.2, 0.537, 0.75, 0.08, Math.PI / 2, 0.15), 0.5);
  addDecal(body, DC.gulp, decal(0.22, 0.22, -0.537, 0.62, -0.05, -Math.PI / 2), 0.4);
  addDecal(body, DC.tape, decal(0.36, 0.09, 0.08, 0.93, -0.3292, Math.PI, 0.04), 0.3);
  addDecal(body, DC.spray, decal(0.3, 0.3, -0.25, 0.85, 0.3305, 0), 0.3);
  addDecal(body, DC.collage, decal(0.3, 0.3, 0.5375, 0.62, -0.17, Math.PI / 2), 0.3);
  addDecal(body, DC.stencil, decal(0.34, 0.085, -0.5375, 0.4, 0.12, -Math.PI / 2), 0.3);
  // lever slot (left front) with its cream handle sticking out, browning knob (right low)
  body.add(box(0.035, 0.46, 0.02, -0.36, 0.72, -0.33), PAL.inkDark);
  body.add(rbox(0.26, 0.07, 0.12, 0.025, -0.46, 0.84, -0.38, 0, 0, 0.12, 1), PAL.warmCream, { outline: true, rust: 0.3 });
  body.add(disc(0.055, 0.07, 8, 0.36, 0.36, -0.355), PAL.tan, smallOut());
  body.add(box(0.012, 0.05, 0.02, 0.36, 0.38, -0.395), PAL.inkDark);
  // frayed cord tail out of the back-left
  body.add(tube(-0.3, 0.3, 0.33, -0.36, 0.12, 0.6, 0.026, 4), PAL.inkDark);
  body.add(tube(-0.36, 0.12, 0.6, -0.22, 0.03, 0.88, 0.022, 4), PAL.inkDark);
  body.add(tube(-0.22, 0.03, 0.88, -0.4, 0.02, 1.1, 0.018, 3), PAL.inkDark);
  for (let k = 0; k < 4; k++) body.add(tube(-0.4, 0.02, 1.1, -0.44 + k * 0.03, 0.03 + (k % 2) * 0.02, 1.18 + (k % 3) * 0.02, 0.006, 3), PAL.army);

  const toast = new PartBuilder(0.024);
  toast.add(rbox(0.34, 0.3, 0.08, 0.04, 0, 0, 0, 0, 0, 0, 1), (_x, y) => (y > 0.11 ? PAL.brick : PAL.bulldozer), { outline: true, glow: PAL.thrusterRing, rust: 0, chip: 0.15 });

  const foot = new PartBuilder(0.022);
  foot.add(cyl(0.065, 0.075, 0.16, 6, 0, -0.08, 0), DARK2, smallOut());
  foot.add(rbox(0.17, 0.06, 0.2, 0.02, 0, -0.17, -0.02, 0, 0, 0, 1), DARK, smallOut());

  // taller, narrower than built: the ref toaster stands upright
  const bg = body.build().translate(0, -0.24, 0).scale(0.93, 1.15, 1).translate(0, 0.24, 0);
  return { parts: { body: bg, toast: T(toast.build(), 0, 0, 0, 0, 0, 0, 1, 1.15, 1), foot: foot.build() } };
}
export const SWARMER = {
  toast: [[-0.223, 1.2, 0], [0.223, 1.2, 0]],
  feet: [[-0.34, 0.19, -0.2], [0.34, 0.19, -0.2], [-0.34, 0.19, 0.2], [0.34, 0.19, 0.2]],
  eyes: [[-0.14, 1.0, -0.345], [0.12, 1.0, -0.345]], eyeR: 0.075,
  depth: 0.34, top: 1.22,
};

// ---------------------------------------------------------------- 1 Shooter: fan egg on tracks
const EGG = [V2(0.001, 0.45), V2(0.5, 0.45), V2(0.64, 0.52), V2(0.69, 0.62), V2(0.73, 0.85), V2(0.745, 1.05), V2(0.745, 1.17),
  V2(0.73, 1.37), V2(0.69, 1.58), V2(0.6, 1.8), V2(0.42, 1.97), V2(0.001, 2.06)];
export function eggR(y: number) {
  for (let i = 0; i < EGG.length - 1; i++) {
    const a = EGG[i], b = EGG[i + 1];
    if (y >= a.y && y <= b.y) return a.x + ((y - a.y) / (b.y - a.y || 1)) * (b.x - a.x);
  }
  return 0.3;
}
export function buildShooter(): RobotModel {
  const body = new PartBuilder(0.032).ao(0.4, 1.3, 0.68);
  // cream egg, one orange belly band (y 0.98-1.16), tan base ring
  const eggTone: Num3 = (_x, y) => (y < 0.6 ? PAL.tan : y > 0.95 && y < 1.18 ? PAL.safetyOrange : PAL.warmCream);
  // band + base as their own rings so the colour edges are crisp, not per-face stair steps
  const egg = new Lathe(EGG.map((p) => p.clone()), sg(14));
  body.add(egg, eggTone, { outline: true, rust: ramp(0.55, 0.75, 1.5, 0.22), chip: 0.55 });
  body.add(T(new Cyl(0.752, 0.752, 0.17, sg(14), 1, true), 0, 1.07, 0), PAL.safetyOrange, { rust: 0.4, chip: 0.6 });
  body.add(T(new Cyl(0.756, 0.756, 0.025, sg(14), 1, true), 0, 0.975, 0), PAL.inkDark, {});
  // fan housing (dark ring + plum back) and the cage over it
  // a deep cowl standing off the egg: dark sleeve, sooty inside, blades sunk in it, cage on the mouth
  const fy = FAN_Y;
  body.add(T(new Cyl(0.36, 0.36, 0.24, sg(16), 1, true), 0, fy, -0.73, Math.PI / 2), DARK, OUT);
  body.add(inward(T(new Cyl(0.33, 0.33, 0.24, sg(16), 1, true), 0, fy, -0.73, Math.PI / 2)), 0x1c1814, { rust: 0, chip: 0 });
  body.add(disc(0.34, 0.02, 16, 0, fy, -0.715), 0x3a2630, { rust: 0.2, chip: 0 });
  body.add(T(new Tor(0.345, 0.028, 4, sg(18)), 0, fy, -0.85), PAL.steel, { rust: 0.5, chip: 0.8 });
  if (LOD >= 1) {
    body.add(T(new Tor(0.22, 0.011, 3, sg(14)), 0, fy, -0.865), PAL.steel, { rust: 0.4 });
    body.add(T(new Tor(0.11, 0.011, 3, sg(10)), 0, fy, -0.868), PAL.steel, { rust: 0.4 });
  }
  for (let k = 0; k < 4; k++) body.add(box(0.68, 0.016, 0.016, 0, fy, -0.87, 0, 0, (k * Math.PI) / 4), PAL.steel, { rust: 0.4 });
  body.add(disc(0.05, 0.05, 6, 0, fy, -0.875), DARK, {});
  // porthole eyes (ink rims; the eyeballs are the shared eye part), speaker grille, sticker, tag
  for (const sx of [-1, 1]) {
    const x = 0.2 * sx, y = 1.86, r = eggR(y), z = -Math.sqrt(r * r - x * x) - 0.004;
    body.add(T(new Tor(0.088, 0.026, 4, sg(10)), x, y, z, 0, -Math.asin(x / r)), PAL.inkDark, {});
  }
  body.add(rbox(0.16, 0.05, 0.08, 0.02, 0, 2.02, -0.3, -0.5, 0, 0, 1), PAL.tan, smallOut());
  addDecal(body, DC.tagMag, decalOn(0.745, 0.36, 0.18, 0.3, 0.82, -0.1), 0.35);
  addDecal(body, DC.price, decalOn(0.745, 0.16, 0.16, -0.38, 0.9, 0.06), 0.35);
  addDecal(body, DC.number, decalOn(0.735, 0.14, 0.14, -0.46, 1.32, 0.1), 0.3);
  addDecal(body, DC.collage, decalOn(0.745, 0.3, 0.3, 0.44, 1.3), 0.3);
  addDecal(body, DC.tape, decalOn(0.745, 0.34, 0.085, -0.08, 0.72, 0.05), 0.3);
  addDecal(body, DC.stencil, decalOn(0.745, 0.32, 0.08, -0.1, 0.88), 0.3);
  addDecal(body, DC.spray, decal(0.34, 0.34, 0, 1.3, 0.752, 0), 0.3);
  addDecal(body, DC.hanko, decalOn(0.73, 0.08, 0.08, 0.5, 1.62), 0.2);
  // red-ball antenna on the left shoulder
  body.add(rbox(0.08, 0.18, 0.12, 0.02, -0.66, 1.62, 0.1, 0, 0, 0, 1), PAL.tan, smallOut());
  body.add(tube(-0.66, 1.68, 0.1, -0.84, 2.95, 0.16, 0.012, 4), PAL.inkDark);
  body.add(T(new Sph(0.07, 7, 5), -0.84, 2.98, 0.16), PAL.toasterRed, OUT);
  // undercarriage
  body.add(rbox(0.66, 0.22, 0.6, 0.05, 0, 0.38, 0, 0, 0, 0, 1), DARK, OUT);

  const fan = new PartBuilder(0.012);
  for (let k = 0; k < 5; k++) {
    const a = (k * Math.PI * 2) / 5;
    fan.add(T(new Box(0.15, 0.27, 0.014), Math.sin(a) * 0.165, Math.cos(a) * 0.165, 0, 0.4, 0, -a), 0x5a3a2c, { glow: PAL.thrusterRing, rust: 0.5, chip: 0.3 });
  }
  fan.add(disc(0.06, 0.04, 6, 0, 0, 0), DARK, { glow: PAL.thrusterRing });

  const track = new PartBuilder(0.025);
  track.add(rbox(0.26, 0.3, 0.8, 0.12, 0, 0, 0, 0, 0, 0, 1), DARK, OUT);
  for (let k = 0; k < 3; k++) track.add(axle(0.095, 0.28, 8, 0, -0.03, -0.25 + k * 0.25), DARK2, { rust: 0.5 });
  if (LOD >= 1) for (let k = 0; k < 6; k++) track.add(box(0.28, 0.035, 0.05, 0, 0.16, -0.3 + k * 0.12), DARK2, {});

  const arm = new PartBuilder(0.02);
  arm.add(axle(0.08, 0.14, 7, 0, 0, 0), PAL.army, smallOut());
  arm.add(cyl(0.035, 0.035, 0.52, 5, 0, -0.28, 0), PAL.warmCream, { outline: LOD >= 2, rust: 0.4 });
  arm.add(rbox(0.08, 0.08, 0.08, 0.02, 0, -0.56, 0, 0, 0, 0, 1), PAL.inkDark, smallOut());
  arm.add(box(0.03, 0.1, 0.06, -0.03, -0.64, -0.02, 0, 0, 0.2), PAL.warmCream, {});
  arm.add(box(0.03, 0.1, 0.06, 0.03, -0.64, -0.02, 0, 0, -0.2), PAL.warmCream, {});

  // slimmer egg than built (the ref is tall and narrow)
  return { parts: { body: body.build().translate(0, -0.45, 0).scale(SH_W, SH_H, SH_W).translate(0, 0.45, 0), fan: T(fan.build(), 0, 0, 0, 0, 0, 0, 0.86), track: track.build(), arm: arm.build() } };
}
const SH_W = 0.8, SH_H = 1.08, FAN_Y = 1.44;
const shY = (y: number) => 0.45 + (y - 0.45) * SH_H;
export const SHOOTER = {
  fan: [0, shY(FAN_Y), -0.77 * SH_W],
  tracks: [[-0.34, 0.17, 0], [0.34, 0.17, 0]],
  arms: [[-0.82 * SH_W, shY(1.2), 0], [0.82 * SH_W, shY(1.2), 0]],
  eyes: [] as number[][], eyeR: 0.07,
  depth: 0.5, top: 2.25,
};
{
  const y = 1.86, r = eggR(y);
  SHOOTER.eyes = [-0.2, 0.2].map((x) => [x * SH_W, shY(y), (-Math.sqrt(r * r - x * x) + 0.01) * SH_W]);
}

// ---------------------------------------------------------------- 2 Brute / 5 Metal Brute: boiler
export interface BruteInks { body: number; dome: number; shoulder: number; seam: number; rivet: number; rust: number; metal: boolean }
export function buildBrute(s: number, k: BruteInks): RobotModel {
  const body = new PartBuilder(0.038).ao(0.55, 1.7, 0.66);
  // torso: a tall rounded boiler, mint going to rust toward the bottom
  const rustLow: Num3 = ramp(0.7, Math.min(1, k.rust * 1.75), 1.9, k.rust * 0.4);
  body.add(rbox(1.56, 1.42, 1.16, 0.3, 0, 1.36, 0), k.body, { outline: true, rust: rustLow, chip: 0.7 });
  // dome head
  body.add(T(new Sph(0.7, sg(12), sg(6), 0, Math.PI * 2, 0, Math.PI / 2), 0, 2.02, 0, 0, 0, 0, 1.08, 1.0, 0.8), k.dome, { outline: true, rust: 0.28, chip: 0.5 });
  body.add(cyl(0.72 * 1.08, 0.74 * 1.08, 0.06, 16, 0, 2.03, 0, 0, 0, 0, 1), k.seam, { rust: 0.5 });
  body.add(cyl(0.12, 0.15, 0.1, 8, 0, 2.74, 0), k.dome, smallOut());
  body.add(T(new Sph(0.07, 6, 4), 0, 2.81, 0), k.seam, {});
  // plate seams (dark, never lit) and a chest vent that glows hot during the windup
  body.add(box(1.57, 0.022, 1.17, 0, 1.15, 0), k.seam, { rust: 0.6 });
  body.add(box(0.022, 0.9, 0.02, 0.42, 1.55, -0.585), k.seam, { rust: 0.6 });
  for (let i = 0; i < 3; i++) {
    body.add(box(0.3, 0.035, 0.02, -0.12, 1.86 - i * 0.075, -0.586), DARK, {});
    body.add(box(0.27, 0.018, 0.02, -0.12, 1.86 - i * 0.075, -0.592), 0x5a2a18, { glow: PAL.thrusterRing, rust: 0, chip: 0 });
  }
  // rivets down the front panel line and around the seams
  const rv = (x: number, y: number, z: number) => body.add(T(new THREE.OctahedronGeometry(0.03, 0), x, y, z), k.rivet, { rust: 0.6 });
  for (let i = 0; i < 6; i++) rv(0.5, 1.12 + i * 0.16, -0.592);
  for (let i = 0; i < 7; i++) rv(-0.6 + i * 0.2, 1.22, -0.592);
  if (LOD >= 1) for (let i = 0; i < 9; i++) { const a = -1.1 + i * 0.275; rv(Math.sin(a) * 0.76, 2.09, -Math.cos(a) * 0.6); }
  // armored shoulders
  for (const sx of [-1, 1]) {
    body.add(T(new Sph(0.42, sg(10), sg(6), 0, Math.PI * 2, 0, Math.PI / 2), 0.86 * sx, 1.82, 0, 0, 0, 0, 1, 0.9, 1.1), k.shoulder, { outline: true, rust: 0.35, chip: 0.6 });
    body.add(cyl(0.43, 0.43, 0.1, 10, 0.86 * sx, 1.82, 0, 0, 0, 0, 1), k.seam, {});
    // a plate seam arching over each pauldron, riveted along its length and round the rim
    body.add(T(new Tor(0.425, 0.014, 3, sg(12), Math.PI), 0.86 * sx, 1.82, 0, 0, Math.PI / 2, 0, 1.1, 0.9, 1), k.seam, {});
    for (let i = 0; i < 5; i++) { const a = 0.35 + i * 0.6; rv(0.86 * sx, 1.82 + Math.sin(a) * 0.44 * 0.9, Math.cos(a) * 0.44 * 1.1); }
    if (LOD >= 1) for (let i = 0; i < 6; i++) { const a = i * 1.05 + 0.3; rv(0.86 * sx + Math.cos(a) * 0.44, 1.86, Math.sin(a) * 0.48); }
  }
  // undercarriage + stickers, drips, the face (tiny mouth dots)
  body.add(cyl(0.68, 0.6, 0.18, 12, 0, 0.62, 0), DARK, OUT);
  addDecal(body, k.metal ? DC.stripes : DC.gulp, decal(k.metal ? 0.5 : 0.24, 0.24, -0.34, 1.62, -0.586, Math.PI, 0.05), 0.3);
  addDecal(body, k.metal ? DC.plate : DC.tagInk, decal(k.metal ? 0.3 : 0.5, k.metal ? 0.3 : 0.25, 0.1, 0.95, -0.587), 0.4);
  addDecal(body, DC.warn, decal(0.22, 0.22, 0.789, 1.5, -0.1, Math.PI / 2), 0.4);
  addDecal(body, DC.tagMag, decal(0.5, 0.25, -0.789, 1.0, 0.05, -Math.PI / 2), 0.4);
  addDecal(body, DC.collage, decal(0.34, 0.34, -0.36, 1.25, -0.5875), 0.35);
  addDecal(body, DC.stencil, decal(0.44, 0.11, 0.12, 1.42, -0.5875), 0.3);
  addDecal(body, DC.tape, decal(0.3, 0.075, 0.12, 1.97, -0.5875, Math.PI, -0.05), 0.3);
  addDecal(body, DC.spray, decal(0.55, 0.55, 0, 1.4, 0.5875, 0), 0.3);
  addDecal(body, DC.price, decal(0.18, 0.18, -0.7895, 1.55, -0.28, -Math.PI / 2), 0.3);
  addDecal(body, DC.number, decal(0.18, 0.18, 0.7895, 1.05, 0.22, Math.PI / 2), 0.3);
  body.add(T(new Sph(0.03, 5, 4), 0.06, 2.3, -0.545), PAL.inkDark);
  body.add(T(new Sph(0.03, 5, 4), 0.16, 2.3, -0.54), PAL.inkDark);
  if (k.metal) {
    // pressure gauge + smokestack + boss trim
    addDecal(body, DC.dial, decal(0.3, 0.3, 0.3, 1.78, -0.587), 0.1);
    body.add(cyl(0.15, 0.19, 0.7, 8, -0.3, 2.65, 0.4), PAL.gunmetal, { outline: true, rust: 0.6 });
    body.add(cyl(0.21, 0.21, 0.08, 8, -0.3, 3.02, 0.4), PAL.gunmetal, OUT);
    for (const sx of [-1, 1]) body.add(T(new THREE.ConeGeometry(0.1, 0.32, 5), 0.86 * sx, 2.25, 0, 0, 0, sx * -0.3), PAL.brass, OUT);
  }
  const lever = new PartBuilder(0.025);
  lever.add(disc(0.075, 0.08, 8, 0, 0, 0), k.seam, smallOut());
  lever.add(rbox(0.07, 0.56, 0.07, 0.025, 0, -0.3, -0.02, 0, 0, 0, 1), k.metal ? PAL.brass : PAL.warmCream, { outline: true, rust: 0.3 });
  lever.add(T(new Sph(0.075, 6, 4), 0, -0.6, -0.02), PAL.inkDark, smallOut());

  // short stocky legs: hip ball, thigh, a plated shin, and a big round-toed boot
  const leg = new PartBuilder(0.03);
  leg.add(T(new Sph(0.17, sg(8), sg(6)), 0, -0.04, 0), DARK2, OUT);
  leg.add(cyl(0.12, 0.13, 0.2, 8, 0, -0.18, 0), DARK, {});
  leg.add(rbox(0.31, 0.22, 0.33, 0.07, 0, -0.33, 0, 0, 0, 0, 2), k.metal ? PAL.gunmetal : 0x6e5638, { outline: true, rust: 0.75, chip: 0.8 });
  leg.add(cyl(0.15, 0.15, 0.04, 8, 0, -0.455, 0), DARK2, {});
  leg.add(rbox(0.42, 0.15, 0.5, 0.06, 0, -0.53, -0.04, 0, 0, 0, 2), DARK, OUT);
  leg.add(T(new Sph(0.21, sg(8), sg(5), 0, Math.PI * 2, 0, Math.PI / 2), 0, -0.6, -0.28, 0, 0, 0, 1, 0.62, 0.72), DARK, OUT);
  leg.add(box(0.44, 0.035, 0.66, 0, -0.603, -0.1), 0x1a1612, {});

  const arm = new PartBuilder(0.03);
  arm.add(rbox(0.34, 0.48, 0.44, 0.1, 0, -0.25, 0), k.shoulder, { outline: true, rust: 0.45, chip: 0.6 });
  arm.add(cyl(0.11, 0.11, 0.12, 6, 0, -0.52, 0), PAL.inkDark, {});
  arm.add(rbox(0.28, 0.38, 0.38, 0.08, 0, -0.74, 0, 0, 0, 0, 1), k.body, { outline: true, rust: 0.65, chip: 0.5 });
  arm.add(cyl(0.09, 0.09, 0.12, 6, 0, -0.96, 0), PAL.inkDark, {});
  arm.add(T(new Tor(0.12, 0.04, 4, sg(9), Math.PI * 1.35), 0, -1.12, 0, 0, Math.PI / 2, Math.PI * 0.55), PAL.inkDark, OUT);

  const parts = { body: body.build(), lever: lever.build(), leg: leg.build(), arm: arm.build() };
  if (s !== 1) for (const g of Object.values(parts)) g.scale(s, s, s);
  for (const g of Object.values(parts)) g.computeBoundingSphere();
  return { parts };
}
export const BRUTE = {
  lever: [0.3, 2.3, -0.5],
  legs: [[-0.4, 0.62, 0], [0.4, 0.62, 0]],
  arms: [[-1.02, 1.84, 0], [1.02, 1.84, 0]],
  eyes: [[-0.1, 2.44, -0.5], [0.13, 2.44, -0.5]], eyeR: 0.055,
  depth: 0.6, top: 2.85,
};

// ---------------------------------------------------------------- 3 Sniper: olive dome on two wheels
/** cooler, greener olive than PAL.olive so the sniper doesn't melt into the gold grass */
const SN_OLIVE = 0x7a8748, SN_OLIVE_D = 0x5c6838;
export function buildSniper(): RobotModel {
  const body = new PartBuilder(0.03).ao(0.3, 1.2, 0.7);
  const can = [V2(0.001, 0.32), V2(0.36, 0.32), V2(0.48, 0.42), V2(0.54, 0.6), V2(0.555, 0.9), V2(0.55, 1.24), V2(0.54, 1.44), V2(0.001, 1.44)];
  body.add(new Lathe(can, sg(12)), SN_OLIVE, { outline: true, rust: ramp(0.45, 0.65, 1.35, 0.25), chip: 0.55 });
  body.add(cyl(0.6, 0.6, 0.07, 16, 0, 1.47, 0), PAL.army, { outline: true, rust: 0.3 });
  body.add(T(new Sph(0.5, sg(12), sg(5), 0, Math.PI * 2, 0, Math.PI / 2), 0, 1.5, 0, 0, 0, 0, 1, 0.95, 1), SN_OLIVE, { outline: true, rust: 0.3, chip: 0.5 });
  body.add(cyl(0.07, 0.08, 0.07, 6, 0, 1.98, 0), PAL.army, smallOut());
  // tall whip antenna + a stub
  body.add(tube(0, 1.98, 0, 0.02, 3.1, 0.04, 0.011, 3), PAL.inkDark);
  body.add(tube(0.28, 1.82, 0.12, 0.3, 2.05, 0.14, 0.014, 3), PAL.inkDark);
  // eye stalks
  for (const sx of [-1, 1]) body.add(T(new Cyl(0.045, 0.055, 0.17, sg(7)), 0.15 * sx, 1.7, -0.5, Math.PI / 2), PAL.army, smallOut());
  // chrome band + rifle clamp
  body.add(cyl(0.57, 0.57, 0.1, 16, 0, 1.12, 0), PAL.chrome, { outline: true, rust: 0.1, chip: 0.1 });
  body.add(rbox(0.16, 0.18, 0.14, 0.03, 0.5, 1.12, -0.3, 0, 0, 0, 1), PAL.chrome, smallOut());
  // fenders over the wheels, undercarriage, axle
  // axle housing out to the big side wheels (no fenders: the wheels are the silhouette)
  body.add(cyl(0.38, 0.32, 0.12, 10, 0, 0.28, 0), DARK, OUT);
  body.add(tube(-0.62, 0.58, 0.1, 0.62, 0.58, 0.1, 0.06, 6), DARK);
  for (const sx of [-1, 1]) body.add(rbox(0.16, 0.2, 0.3, 0.04, 0.5 * sx, 0.58, 0.1, 0, 0, 0, 1), SN_OLIVE_D, { outline: true, rust: 0.7, chip: 0.8 });
  // stickers, graffiti, drips
  addDecal(body, DC.patch, decalOn(0.555, 0.2, 0.2, -0.12, 1.3, 0.05), 0.3);
  addDecal(body, DC.hanko, decalOn(0.555, 0.08, 0.08, -0.33, 1.3), 0.15);
  addDecal(body, DC.tagInk, decalOn(0.555, 0.42, 0.21, -0.05, 0.78, 0.04), 0.3);
  addDecal(body, DC.number, decal(0.18, 0.18, 0.562, 0.95, 0.05, Math.PI / 2), 0.35);
  addDecal(body, DC.collage, decalOn(0.555, 0.28, 0.28, 0.24, 1.3), 0.3);
  addDecal(body, DC.stencil, decalOn(0.555, 0.42, 0.105, 0.0, 0.98), 0.3);
  addDecal(body, DC.tape, decalOn(0.555, 0.3, 0.075, -0.25, 0.6, -0.1), 0.3);
  addDecal(body, DC.spray, decal(0.34, 0.34, 0, 0.95, 0.566, 0), 0.3);
  addDecal(body, DC.warn, decal(0.16, 0.16, -0.562, 1.3, 0.05, -Math.PI / 2), 0.3);
  addDecal(body, DC.price, decal(0.16, 0.16, 0.5625, 1.3, -0.12, Math.PI / 2, 0.1), 0.3);

  const wheel = new PartBuilder(0.03);
  // big tyre, olive dished rim with a dark hub and lug bolts
  wheel.add(axle(0.58, 0.2, 16, 0, 0, 0), DARK, OUT);
  wheel.add(T(new Tor(0.5, 0.035, 3, sg(12)), 0.105, 0, 0, 0, Math.PI / 2), 0x1e1a16, {});
  wheel.add(axle(0.4, 0.22, 14, 0, 0, 0), SN_OLIVE, { rust: 0.75, chip: 0.7 });
  wheel.add(axle(0.15, 0.25, 8, 0, 0, 0), DARK2, {});
  for (let k = 0; k < 4; k++) { const a = (k / 4) * Math.PI * 2; wheel.add(T(new THREE.OctahedronGeometry(0.035, 0), 0.125, Math.sin(a) * 0.1, Math.cos(a) * 0.1), PAL.brass, {}); wheel.add(T(new THREE.OctahedronGeometry(0.035, 0), -0.125, Math.sin(a) * 0.1, Math.cos(a) * 0.1), PAL.brass, {}); }
  for (let k = 0; k < 4; k++) wheel.add(box(0.23, 0.05, 0.3, 0, 0, 0, (k * Math.PI) / 4, 0, 0), SN_OLIVE_D, { rust: 0.6 });

  const rifle = new PartBuilder(0.018);
  // a long dark barrel with brass bands and a muzzle brake, a receiver, a shaped wooden stock
  rifle.add(cyl(0.028, 0.034, 1.15, 6, 0, 0, -0.64, Math.PI / 2), PAL.gunmetal, { outline: true, rust: 0.3, chip: 0.9 });
  for (const bz of [-0.36, -0.7, -1.02]) rifle.add(cyl(0.044, 0.044, 0.04, 8, 0, 0, bz, Math.PI / 2), PAL.brass, { rust: 0.3, chip: 0.4 });
  rifle.add(cyl(0.05, 0.05, 0.13, 6, 0, 0, -1.25, Math.PI / 2), DARK, smallOut());
  rifle.add(box(0.11, 0.02, 0.05, 0, 0, -1.24), DARK2, {});
  rifle.add(rbox(0.1, 0.15, 0.44, 0.02, 0, -0.01, 0.0, 0, 0, 0, 1), PAL.gunmetal, { outline: true, rust: 0.4, chip: 0.9 });
  rifle.add(rbox(0.06, 0.15, 0.08, 0.02, 0, -0.13, 0.13, 0.4, 0, 0, 1), PAL.wood, smallOut());
  rifle.add(rbox(0.075, 0.18, 0.32, 0.03, 0, -0.05, 0.38, -0.12, 0, 0, 1), PAL.wood, { outline: true, rust: 0.2, chip: 0.6 });
  rifle.add(box(0.085, 0.2, 0.03, 0, -0.07, 0.55, -0.12), DARK, {});
  // a fat scope: dark tube, bell, eyepiece, brass rings, a turret knob, a glinting lens
  rifle.add(cyl(0.066, 0.066, 0.4, 8, 0, 0.15, -0.14, Math.PI / 2), DARK, OUT);
  rifle.add(cyl(0.09, 0.066, 0.09, 8, 0, 0.15, -0.38, Math.PI / 2), DARK, OUT);
  rifle.add(cyl(0.08, 0.066, 0.06, 8, 0, 0.15, 0.08, Math.PI / 2), DARK, smallOut());
  for (const rz of [-0.24, 0.0]) rifle.add(cyl(0.074, 0.074, 0.03, 8, 0, 0.15, rz, Math.PI / 2), PAL.brass, { rust: 0.2 });
  rifle.add(cyl(0.032, 0.032, 0.07, 6, 0, 0.24, -0.12), DARK2, {});
  rifle.add(disc(0.072, 0.012, 10, 0, 0.15, -0.428), PAL.shadowTeal, { glow: PAL.sunCore, rust: 0, chip: 0 });
  rifle.add(box(0.035, 0.09, 0.035, 0, 0.08, -0.24), DARK2, {});
  rifle.add(box(0.035, 0.09, 0.035, 0, 0.08, 0.0), DARK2, {});

  return { parts: { body: body.build(), wheel: wheel.build(), rifle: T(rifle.build(), 0, 0, 0, 0, 0, 0, RIFLE_S) } };
}
const RIFLE_S = 1.7;
export const SNIPER = {
  wheels: [[-0.72, 0.58, 0.1], [0.72, 0.58, 0.1]], wheelR: 0.58,
  rifle: [0.54, 1.22, -0.26],
  scope: [0, 0.15 * RIFLE_S, -0.43 * RIFLE_S],
  eyes: [[-0.15, 1.7, -0.6], [0.15, 1.7, -0.6]], eyeR: 0.075,
  depth: 0.55, top: 2.0,
};

// ---------------------------------------------------------------- 4 Bomber: front-load washer
export function buildBomber(): RobotModel {
  const body = new PartBuilder(0.032).ao(0.4, 1.5, 0.68);
  // cream top third, washer-blue lower body with a cream pinstripe
  body.add(rbox(1.3, 0.56, 1.06, 0.08, 0, 1.68, 0), PAL.warmCream, { outline: true, rust: ramp(1.45, 0.45, 1.95, 0.3), chip: 0.6 });
  body.add(rbox(1.3, 0.98, 1.06, 0.08, 0, 0.96, 0), PAL.washerBand, { outline: true, rust: ramp(0.5, 0.95, 1.35, 0.45), chip: 0.65 });
  body.add(box(1.315, 0.035, 1.075, 0, 1.36, 0), PAL.warmCream, {});
  body.add(box(1.315, 0.02, 1.075, 0, 1.31, 0), PAL.washerBlue, {});
  body.add(box(1.28, 0.012, 0.012, 0, 0.6, -0.536), PAL.inkDark);
  for (const bx of [-0.6, 0.6]) body.add(box(0.012, 0.6, 0.012, bx, 0.95, -0.536), PAL.inkDark);
  // knob eyes housings, a slot of vents
  for (const sx of [-1, 1]) body.add(disc(0.105, 0.07, 12, 0.36 * sx, 1.8, -0.54), PAL.tan, smallOut());
  addDecal(body, DC.vent, decal(0.2, 0.12, 0.0, 1.62, -0.535), 0.2);
  // porthole: thick cream ring, dark glass (the drum + door glow are their own part)
  // porthole: a deep cream bezel sleeve, sooty inner wall, dark glass sunk at the back, one glint
  body.add(T(new Tor(0.3, 0.075, 6, sg(18)), -0.1, 1.08, -0.66), PAL.chrome, { outline: true, rust: 0.15, chip: 0 });
  body.add(T(new Tor(0.245, 0.018, 3, sg(12)), -0.1, 1.08, -0.545), PAL.chrome, { rust: 0, chip: 0 });
  body.add(T(new Cyl(0.35, 0.35, 0.13, sg(18), 1, true), -0.1, 1.08, -0.595, Math.PI / 2), PAL.paperCream, { rust: 0.45, chip: 0.6 });
  body.add(inward(T(new Cyl(0.235, 0.235, 0.13, sg(16), 1, true), -0.1, 1.08, -0.595, Math.PI / 2)), DARK, { rust: 0, chip: 0 });
  body.add(disc(0.24, 0.01, 16, -0.1, 1.08, -0.535), 0x182220, { rust: 0, chip: 0 });
  body.add(T(new Tor(0.17, 0.013, 3, 8, 1.1), -0.1, 1.08, -0.6, 0, 0, 1.75), 0xf4eedc, { rust: 0, chip: 0, jitter: 0 });
  // stickers, scribbles, drips
  addDecal(body, DC.sale, decal(0.3, 0.15, 0.42, 0.66, -0.536, Math.PI, 0.08), 0.45);
  addDecal(body, DC.tagCream, decal(0.46, 0.23, -0.2, 0.62, -0.536), 0.4);
  addDecal(body, DC.warn, decal(0.16, 0.16, 0.45, 1.25, -0.536), 0.3);
  addDecal(body, DC.price, decal(0.24, 0.24, 0.656, 1.0, 0.1, Math.PI / 2), 0.4);
  addDecal(body, DC.tagMag, decal(0.5, 0.25, -0.656, 0.9, 0.0, -Math.PI / 2), 0.4);
  addDecal(body, DC.collage, decal(0.26, 0.26, 0.45, 0.95, -0.5362), 0.35);
  addDecal(body, DC.tape, decal(0.4, 0.1, -0.35, 1.5, -0.5362, Math.PI, 0.06), 0.3);
  addDecal(body, DC.stencil, decal(0.4, 0.1, -0.6565, 0.62, 0.1, -Math.PI / 2), 0.3);
  addDecal(body, DC.spray, decal(0.5, 0.5, 0, 1.0, 0.5362, 0), 0.3);
  addDecal(body, DC.number, decal(0.16, 0.16, 0.3, 1.72, 0.5362, 0), 0.3);
  // undercarriage + shoulder socket
  body.add(rbox(1.1, 0.16, 0.86, 0.04, 0, 0.42, 0, 0, 0, 0, 1), DARK, OUT);
  // undercarriage: chassis rails, a cross pipe and hydraulic rams down to the hips
  for (const sx of [-1, 1]) {
    body.add(box(0.09, 0.09, 1.0, 0.42 * sx, 0.36, 0), DARK2, { rust: 0.6 });
    body.add(tube(0.3 * sx, 0.4, -0.3, 0.36 * sx, 0.52, 0.0, 0.035, 5), PAL.steel, { rust: 0.5 });
  }
  body.add(tube(-0.45, 0.33, 0.3, 0.45, 0.33, 0.3, 0.045, 6), DARK2);
  body.add(rbox(0.2, 0.22, 0.3, 0.05, 0.68, 1.55, 0.05, 0, 0, 0, 1), DARK, smallOut());

  const lid = new PartBuilder(0.025);
  lid.add(rbox(1.06, 0.07, 0.84, 0.03, 0, 0.035, -0.42, 0, 0, 0, 1), PAL.washerBand, { outline: true, rust: 0.35, chip: 0.5 });
  lid.add(box(0.98, 0.02, 0.76, 0, -0.004, -0.42), PAL.warmCream, {});
  lid.add(box(0.36, 0.07, 0.09, 0, 0, 0.0), PAL.inkDark, {});

  const door = new PartBuilder(0.01);
  door.add(disc(0.23, 0.015, 16, 0, 0, 0), 0x1a2624, { glow: PAL.thrusterRing, rust: 0, chip: 0 });
  for (let k = 0; k < 3; k++) {
    const a = (k * Math.PI * 2) / 3;
    door.add(box(0.07, 0.15, 0.03, Math.sin(a) * 0.15, Math.cos(a) * 0.15, -0.02, 0, 0, -a), DARK2, { rust: 0.2 });
  }
  door.add(disc(0.045, 0.03, 6, 0, 0, -0.02), DARK2, {});

  const arm = new PartBuilder(0.025);
  arm.add(axle(0.13, 0.14, 8, 0, 0, 0), PAL.paperCream, smallOut());
  arm.add(tube(0, 0, 0, 0.1, -0.36, -0.05, 0.09, sg(7)), PAL.paperCream, { outline: true, rust: 0.3 });
  arm.add(T(new Sph(0.11, 7, 5), 0.1, -0.38, -0.05), PAL.inkDark, smallOut());
  arm.add(tube(0.1, -0.38, -0.05, 0.12, -0.62, -0.2, 0.075, sg(7)), PAL.paperCream, { outline: true, rust: 0.35 });
  arm.add(rbox(0.17, 0.09, 0.17, 0.03, 0.12, -0.66, -0.22, 0, 0, 0, 1), PAL.inkDark, smallOut());

  const bomb = new PartBuilder(0.025);
  bomb.add(new THREE.IcosahedronGeometry(0.23, LOD >= 1 ? 2 : 1), PAL.gunmetal, { outline: true, rust: 0.3, chip: 0.5 });
  bomb.add(cyl(0.05, 0.06, 0.06, 6, 0, 0.23, 0), PAL.inkDark, {});
  bomb.add(tube(0, 0.25, 0, 0.04, 0.35, 0.02, 0.012, 3), PAL.tan, {});
  bomb.add(T(new THREE.OctahedronGeometry(0.04, 0), 0.04, 0.36, 0.02), PAL.bulldozer, { glow: PAL.thrusterRing, rust: 0 });

  const leg = new PartBuilder(0.025);
  // a bird-kneed leg: hip block, thigh forward to a knee, shin back to a splayed foot
  leg.add(rbox(0.22, 0.16, 0.24, 0.05, 0, -0.04, 0, 0, 0, 0, 1), DARK, smallOut());
  leg.add(tube(0, -0.08, 0, 0, -0.24, -0.12, 0.06, sg(6), 0.07), DARK2, smallOut());
  leg.add(T(new Sph(0.075, 6, 4), 0, -0.25, -0.12), DARK, {});
  leg.add(tube(0, -0.25, -0.12, 0, -0.42, 0.03, 0.045, sg(6), 0.055), PAL.steel, { outline: LOD >= 2, rust: 0.6 });
  leg.add(rbox(0.26, 0.07, 0.38, 0.03, 0, -0.455, -0.06, 0, 0, 0, 1), DARK, smallOut());

  return { parts: { body: body.build(), lid: lid.build(), door: door.build(), arm: arm.build(), bomb: bomb.build(), leg: leg.build() } };
}
export const BOMBER = {
  lid: [0, 1.97, 0.42],
  door: [-0.1, 1.08, -0.548],
  arm: [0.8, 1.55, 0.05],
  hand: [0.12, -0.86, -0.24],
  legs: [[-0.34, 0.5, 0], [0.34, 0.5, 0]],
  eyes: [[-0.36, 1.8, -0.6], [0.36, 1.8, -0.6]], eyeR: 0.075,
  depth: 0.53, top: 2.0,
};

// ---------------------------------------------------------------- 6 Disruptor: microwave on a chair base
export function buildDisruptor(): RobotModel {
  const body = new PartBuilder(0.03).ao(1.2, 1.9, 0.72);
  // cream box with a brick-red band at the bottom
  body.add(rbox(1.24, 0.72, 0.84, 0.06, 0, 1.78, 0), PAL.warmCream, { outline: true, rust: ramp(1.45, 0.8, 2.1, 0.5), chip: 0.8 });
  body.add(rbox(1.26, 0.2, 0.86, 0.05, 0, 1.36, 0, 0, 0, 0, 1), PAL.brick, { outline: true, rust: 0.6, chip: 0.6 });
  // door frame (inset) + keypad panel
  body.add(box(0.8, 0.6, 0.02, -0.16, 1.8, -0.421), PAL.paperCream, {});
  body.add(box(0.014, 0.66, 0.012, 0.27, 1.79, -0.433), PAL.inkDark);
  body.add(rbox(0.05, 0.4, 0.05, 0.02, 0.2, 1.8, -0.45, 0, 0, 0, 1), PAL.steel, {});
  addDecal(body, DC.keypad, decal(0.24, 0.5, 0.43, 1.79, -0.434), 0.2);
  addDecal(body, DC.asis, decal(0.2, 0.2, 0.633, 1.8, 0.1, Math.PI / 2, 0.1), 0.35);
  addDecal(body, DC.tagMag, decal(0.5, 0.25, -0.633, 1.72, 0.0, -Math.PI / 2), 0.4);
  addDecal(body, DC.smiley, decal(0.14, 0.14, 0.45, 1.4, -0.436), 0.3);
  // graffiti over the door, a peeling rust bloom on the top corner, a price tag and a seal
  addDecal(body, DC.scrawl, decal(0.5, 0.2, -0.3, 1.52, -0.4365, Math.PI, -0.08), 0.3);
  addDecal(body, DC.rustBloom, decal(0.42, 0.3, 0.4, 1.97, -0.4362), 0.3);
  addDecal(body, DC.price, decal(0.18, 0.12, -0.5, 2.05, -0.4364, Math.PI, 0.15), 0.4);
  addDecal(body, DC.hanko, decal(0.09, 0.09, 0.56, 1.5, -0.4366), 0.2);
  addDecal(body, DC.tape, decal(0.3, 0.075, 0.05, 2.1, -0.4368, Math.PI, 0.05), 0.3);
  addDecal(body, DC.collage, decal(0.42, 0.42, 0, 1.8, 0.4205, 0), 0.3);
  addDecal(body, DC.stencil, decal(0.4, 0.1, -0.6336, 1.95, 0.0, -Math.PI / 2), 0.3);
  addDecal(body, DC.rustBloom, decal(0.5, 0.4, 0.6336, 1.62, -0.2, Math.PI / 2, 3.1), 0.3);
  // dark underside + the chair column
  body.add(rbox(1.0, 0.1, 0.7, 0.03, 0, 1.22, 0, 0, 0, 0, 1), DARK, OUT);
  body.add(cyl(0.06, 0.07, 0.62, 8, 0, 0.9, 0), PAL.steel, smallOut());
  body.add(cyl(0.11, 0.09, 0.14, 8, 0, 0.62, 0), DARK, smallOut());
  // satellite dish on a stub mast, feed horn
  body.add(cyl(0.035, 0.035, 0.22, 6, -0.18, 2.24, 0.1), PAL.inkDark, {});
  body.add(T(new Sph(0.3, sg(12), 4, 0, Math.PI * 2, 0, 0.9), -0.18, 2.62, 0.1, -0.7, 0, 0.25, 1, 0.45, 1), PAL.paperCream, { outline: true, rust: 0.4, chip: 0.4 });
  body.add(tube(-0.18, 2.4, 0.1, -0.2, 2.68, -0.1, 0.014, 3), PAL.inkDark);
  body.add(T(new Sph(0.035, 5, 4), -0.2, 2.68, -0.1), PAL.toasterRed);
  body.add(tube(0.4, 2.14, 0.2, 0.46, 2.5, 0.24, 0.01, 3), PAL.inkDark);

  // the screen face: three moods as three slots (only one is shown)
  const face = (c: Cell) => { const b = new PartBuilder(0); addDecal(b, c, decal(0.66, 0.66, 0, 0, 0), 0.1); return b.build(); };

  const caster = new PartBuilder(0.02);
  for (let k = 0; k < 5; k++) {
    const a = (k / 5) * Math.PI * 2;
    caster.add(tube(0, 0, 0, Math.sin(a) * 0.42, -0.12, Math.cos(a) * 0.42, 0.035, 5), DARK, {});
    caster.add(T(new Sph(0.06, 6, 4), Math.sin(a) * 0.42, -0.2, Math.cos(a) * 0.42), DARK, {});
  }
  caster.add(cyl(0.1, 0.12, 0.06, 8, 0, 0, 0), DARK, {});

  // spindly salvage legs: cream thigh, orange shin, dark foot
  const leg = new PartBuilder(0.024);
  leg.add(rbox(0.17, 0.42, 0.19, 0.04, 0, -0.22, 0, 0, 0, 0, 1), PAL.paperCream, { outline: true, rust: 0.45 });
  leg.add(cyl(0.05, 0.05, 0.12, 6, 0, -0.47, 0), PAL.inkDark, {});
  leg.add(rbox(0.15, 0.56, 0.17, 0.04, 0, -0.78, 0, 0, 0, 0, 1), PAL.safetyOrange, { outline: true, rust: 0.6 });
  leg.add(rbox(0.19, 0.08, 0.28, 0.02, 0, -1.08, -0.05, 0, 0, 0, 1), DARK, smallOut());

  const arm = new PartBuilder(0.02);
  arm.add(cyl(0.05, 0.05, 0.4, 6, 0, -0.2, 0), PAL.paperCream, smallOut());
  arm.add(T(new Sph(0.06, 6, 4), 0, -0.42, 0), PAL.inkDark, {});
  arm.add(cyl(0.04, 0.04, 0.34, 6, 0, -0.6, 0), PAL.safetyOrange, smallOut());
  arm.add(box(0.03, 0.1, 0.06, -0.03, -0.8, 0, 0, 0, 0.25), PAL.inkDark, {});
  arm.add(box(0.03, 0.1, 0.06, 0.03, -0.8, 0, 0, 0, -0.25), PAL.inkDark, {});

  return { parts: { body: body.build(), smile: face(DC.faceSmile), angry: face(DC.faceAngry), stat: face(DC.faceStatic), caster: caster.build(), leg: leg.build(), arm: arm.build() } };
}
export const DISRUPTOR = {
  face: [-0.16, 1.8, -0.437],
  caster: [0, 0.22, 0],
  legs: [[-0.4, 1.17, 0.0], [0.4, 1.17, 0.0]],
  arm: [0.66, 1.62, 0.05],
  eyes: [] as number[][], eyeR: 0.06,
  depth: 0.42, top: 2.4,
};

// ---------------------------------------------------------------- 7 Engineer: photocopier on three stilts
export function buildEngineer(): RobotModel {
  const body = new PartBuilder(0.032).ao(1.9, 2.4, 0.72);
  // tan copier, one orange panel on the front left, a top lid layer, dark copy glass
  body.add(rbox(1.5, 0.56, 1.06, 0.06, 0, 2.22, 0), PAL.tan, { outline: true, rust: ramp(1.95, 0.85, 2.5, 0.5), chip: 0.85 });
  body.add(rbox(1.42, 0.12, 0.98, 0.04, -0.02, 2.56, 0.02, 0, 0, 0, 1), PAL.paperCream, { outline: true, rust: 0.4, chip: 0.5 });
  body.add(box(0.6, 0.012, 0.6, 0.35, 2.625, 0.05), PAL.shadowTeal, { glow: PAL.windowGlow, rust: 0, chip: 0 });
  // the stepped printer stack: a scanner block on the left of the lid, a feeder on top of that
  body.add(rbox(0.74, 0.26, 0.92, 0.05, -0.36, 2.75, 0.02, 0, 0, 0, 1), PAL.tan, { outline: true, rust: 0.55, chip: 0.85 });
  body.add(rbox(0.5, 0.17, 0.62, 0.04, -0.42, 2.96, 0.12, 0, 0, 0, 1), PAL.paperCream, { outline: true, rust: 0.45, chip: 0.8 });
  body.add(box(0.4, 0.02, 0.4, -0.42, 3.05, 0.1), PAL.warmCream, {});
  body.add(box(0.76, 0.012, 0.012, -0.36, 2.7, -0.462), DARK);
  // side output tray off the left flank, with a sheet of paper in it
  body.add(box(0.46, 0.03, 0.66, -0.98, 2.12, 0.02, 0, 0, -0.22), PAL.paperCream, { outline: true, rust: 0.4 });
  body.add(box(0.4, 0.012, 0.5, -0.98, 2.145, 0.02, 0, 0, -0.22), 0xf2ecdc, { rust: 0.1, chip: 0 });
  body.add(box(0.04, 0.1, 0.66, -1.2, 2.1, 0.02, 0, 0, -0.22), PAL.paperCream, {});
  body.add(box(1.52, 0.012, 0.012, 0, 2.1, -0.534), PAL.inkDark);
  body.add(box(0.012, 0.4, 0.012, -0.2, 2.28, -0.534), PAL.inkDark);
  body.add(rbox(0.5, 0.3, 0.04, 0.02, -0.46, 2.27, -0.53, 0, 0, 0, 1), PAL.safetyOrange, { rust: 0.5, chip: 0.5 });
  addDecal(body, DC.copyPanel, decal(0.56, 0.28, 0.4, 2.36, -0.534), 0.25);
  addDecal(body, DC.tagInk, decal(0.5, 0.25, 0.754, 2.2, 0.1, Math.PI / 2), 0.4);
  addDecal(body, DC.warn, decal(0.2, 0.2, -0.754, 2.25, -0.2, -Math.PI / 2), 0.4);
  addDecal(body, DC.number, decal(0.16, 0.16, -0.754, 2.25, 0.2, -Math.PI / 2), 0.4);
  // graffiti, a rust bloom eating the front corner, a sale sticker, an arrow and a seal
  addDecal(body, DC.scrawl, decal(0.5, 0.2, 0.05, 2.06, -0.5352, Math.PI, 0.06), 0.3);
  addDecal(body, DC.rustBloom, decal(0.42, 0.28, -0.52, 2.08, -0.5354), 0.3);
  addDecal(body, DC.sale, decal(0.24, 0.12, 0.55, 2.45, -0.5356, Math.PI, -0.1), 0.4);
  addDecal(body, DC.arrow, decal(0.14, 0.14, 0.62, 2.12, -0.5358), 0.3);
  addDecal(body, DC.hanko, decal(0.08, 0.08, -0.2, 2.44, -0.536), 0.2);
  addDecal(body, DC.rustBloom, decal(0.5, 0.4, 0.7546, 2.1, -0.25, Math.PI / 2, 0.4), 0.3);
  addDecal(body, DC.collage, decal(0.42, 0.42, 0, 2.25, 0.5352, 0), 0.3);
  addDecal(body, DC.stencil, decal(0.36, 0.09, 0.5, 2.02, -0.5352), 0.3);
  addDecal(body, DC.tape, decal(0.3, 0.07, -0.36, 2.75, -0.4416, Math.PI, 0.04), 0.3);
  addDecal(body, DC.spray, decal(0.26, 0.26, -0.7546, 2.06, -0.02, -Math.PI / 2), 0.3);
  addDecal(body, DC.price, decal(0.14, 0.14, -0.2, 2.96, -0.1916, Math.PI, 0.1), 0.3);
  // paper-tray beak sticking out of the front, output slot
  body.add(box(0.7, 0.03, 0.42, 0.0, 2.04, -0.7, -0.1), PAL.paperCream, { outline: true, rust: 0.3 });
  body.add(box(0.6, 0.012, 0.32, 0.0, 2.065, -0.68, -0.1), PAL.warmCream, {});
  body.add(box(0.74, 0.06, 0.04, 0.0, 2.1, -0.53), PAL.inkDark, {});
  // underside: a dark belly + the hip block the stilts hang from
  body.add(rbox(1.2, 0.14, 0.8, 0.04, 0, 1.9, 0, 0, 0, 0, 1), DARK, OUT);
  body.add(rbox(0.5, 0.22, 0.5, 0.05, 0, 1.75, 0.05, 0, 0, 0, 1), DARK2, smallOut());
  // antennas
  body.add(tube(0.55, 2.62, 0.3, 0.6, 3.1, 0.34, 0.011, 3), PAL.inkDark);
  body.add(tube(0.42, 2.62, 0.35, 0.4, 2.92, 0.38, 0.011, 3), PAL.inkDark);
  body.add(T(new Sph(0.03, 5, 4), 0.6, 3.11, 0.34), PAL.toasterRed);

  // the copy light that sweeps across the glass (telegraph)
  const light = new PartBuilder(0);
  light.add(box(0.08, 0.02, 0.6, 0, 0, 0), PAL.windowGlow, { glow: PAL.sunCore, rust: 0, chip: 0, jitter: 0 });

  // stilts: a thin dark leg with a knee and a tiny foot
  const stilt = new PartBuilder(0.02);
  stilt.add(cyl(0.05, 0.05, 0.14, 6, 0, -0.07, 0), DARK, {});
  stilt.add(cyl(0.035, 0.03, 0.85, 6, 0, -0.55, 0), DARK2, smallOut());
  stilt.add(T(new Sph(0.05, 6, 4), 0, -0.98, 0), DARK, {});
  stilt.add(cyl(0.025, 0.02, 0.72, 5, 0, -1.36, 0), DARK, smallOut());
  stilt.add(cyl(0.07, 0.05, 0.04, 6, 0, -1.72, 0), DARK, {});

  // the wrench arm hanging off the right
  const arm = new PartBuilder(0.02);
  arm.add(cyl(0.035, 0.035, 0.55, 5, 0, -0.27, 0), PAL.inkDark, smallOut());
  arm.add(T(new Sph(0.05, 5, 4), 0, -0.56, 0), PAL.inkDark, {});
  arm.add(cyl(0.03, 0.03, 0.4, 5, 0, -0.78, 0), PAL.steel, smallOut());
  // a hanging grabber: wrist, palm, and three hooked fingers
  arm.add(cyl(0.05, 0.05, 0.06, 6, 0, -1.0, 0), DARK, {});
  arm.add(rbox(0.16, 0.08, 0.16, 0.02, 0, -1.07, 0, 0, 0, 0, 1), DARK2, smallOut());
  for (let k = 0; k < 3; k++) {
    const a = (k / 3) * Math.PI * 2;
    arm.add(T(new Tor(0.09, 0.018, 3, sg(6), Math.PI * 0.9), Math.sin(a) * 0.05, -1.17, Math.cos(a) * 0.05, 0, a + Math.PI / 2, Math.PI * 0.55), PAL.steel, { rust: 0.5 });
  }

  // sits lower than built on shorter stilts (the ref's legs are ~1.3x the body, not 3x)
  return { parts: { body: body.build().translate(0, -EN_DROP, 0), light: light.build(), stilt: stilt.build().scale(1, EN_LEG, 1), arm: arm.build() } };
}
const EN_DROP = 0.44, EN_LEG = (1.74 - EN_DROP) / 1.74;
export const ENGINEER = {
  light: [0.35, 2.635 - EN_DROP, 0.05],
  stilts: [[-0.45, 1.74 - EN_DROP, -0.25], [0.45, 1.74 - EN_DROP, -0.25], [0.0, 1.74 - EN_DROP, 0.38]],
  arm: [0.78, 2.1 - EN_DROP, 0.1],
  eyes: [[-0.14, 2.43 - EN_DROP, -0.55], [0.12, 2.43 - EN_DROP, -0.55]], eyeR: 0.05,
  depth: 0.53, top: 3.06 - EN_DROP,
};
